import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const ProgressContext = createContext();

export const ProgressProvider = ({ children }) => {
  const { user } = useAuth();
  const [solvedQuestions, setSolvedQuestions] = useState(new Set());
  const [aptitudeSolved, setAptitudeSolved] = useState(new Set());
  const [aptitudeAnswers, setAptitudeAnswers] = useState({});

  const resetProgress = () => {
    setSolvedQuestions(new Set());
    setAptitudeSolved(new Set());
    setAptitudeAnswers({});
  };

  useEffect(() => {
    if (!user) {
      setSolvedQuestions(new Set());
      setAptitudeSolved(new Set());
      setAptitudeAnswers({});
      return;
    }

    const fetchProgress = async () => {
      try {
        const res = await api.getProgress(user.id);
        if (res.ok) {
          const data = await res.json();
          setSolvedQuestions(new Set(data.solvedQuestionIds || []));
          setAptitudeSolved(new Set(data.aptitudeSolvedQuestionIds || []));
        }
      } catch (error) {
        console.error('Failed to load progress', error);
      }
    };
    fetchProgress();
  }, [user]);

  const toggleSolved = async (id, category = 'dsa') => {
    if (!user) return;
    const currentlySolved = solvedQuestions.has(id);
    
    // Optimistic Update
    setSolvedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });

    try {
      const res = await api.updateProgress({
        userId: user.id,
        questionId: id,
        category,
        solved: !currentlySolved,
      });
      if (!res.ok) throw new Error('Failed to update progress on server');
    } catch (error) {
      console.error('Progress update failed - Rolling back:', error);
      // Rollback on failure
      setSolvedQuestions(prev => {
        const newSet = new Set(prev);
        if (currentlySolved) newSet.add(id);
        else newSet.delete(id);
        return newSet;
      });
    }
  };

  const markAptitudeSolved = async (id, categoryKey) => {
    if (!user) return;
    if (aptitudeSolved.has(id)) return;

    // Optimistic Update
    setAptitudeSolved(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      const res = await api.updateProgress({
        userId: user.id,
        questionId: id,
        category: `aptitude-${categoryKey}`,
        solved: true,
      });
      if (!res.ok) throw new Error('Failed to mark as solved');
    } catch (error) {
      console.error('Aptitude solve failed - Rolling back:', error);
      // Rollback
      setAptitudeSolved(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const resetAptitudeQuestion = async (id, categoryKey) => {
    if (!user) return;
    const wasSolved = aptitudeSolved.has(id);
    const oldAnswer = aptitudeAnswers[id];

    // Optimistic Reset
    setAptitudeSolved(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setAptitudeAnswers(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    try {
      const res = await api.updateProgress({
        userId: user.id,
        questionId: id,
        category: `aptitude-${categoryKey}`,
        solved: false,
      });
      if (!res.ok) throw new Error('Failed to reset on server');
    } catch (error) {
      console.error('Aptitude reset failed - Rolling back:', error);
      // Rollback both solved state and the answer
      if (wasSolved) {
        setAptitudeSolved(prev => new Set(prev).add(id));
      }
      if (oldAnswer) {
        setAptitudeAnswers(prev => ({ ...prev, [id]: oldAnswer }));
      }
    }
  };

  return (
    <ProgressContext.Provider value={{ 
      solvedQuestions, 
      aptitudeSolved, 
      aptitudeAnswers, 
      setAptitudeAnswers,
      toggleSolved,
      markAptitudeSolved,
      resetAptitudeQuestion,
      resetProgress
    }}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => useContext(ProgressContext);
